// NÓMINA DE LA PERSONA — historial de lo que ya se le registró (Mabel 3-oct-2026, punto 4: «que en los
// perfiles de maestros y staff… quede historial»). Solo lectura: corridas (sueldo fijo + clases), líneas
// manuales (bonos/comisiones) y entregas de comisión, agrupadas por semana. Se registra desde Finanzas › Nómina.
import { useEffect, useState } from 'react';
import { Wallet, Gift, Percent } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getPayrollHistory, PayrollHistory, money } from '../../finances/api';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';

const fromISO = (d: string) => { const [y, m, dd] = d.split('-').map(Number); return new Date(y, m - 1, dd); };
const dia = (d: string, year = false) => fromISO(d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', ...(year ? { year: 'numeric' } : {}) });
/** Semana lun–sáb como en Nómina (el rango interno es lun–dom). */
function semana(from: string, to: string) {
  const f = fromISO(from), t = fromISO(to); t.setDate(t.getDate() - 1);
  const dias = Math.round((t.getTime() - f.getTime()) / 86400000) + 1;
  if (dias >= 28) return f.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  if (dias === 1) return dia(from, true);
  const s = new Date(f); s.setDate(s.getDate() + 5);
  return `${dia(from)} – ${dia(s.toISOString().slice(0, 10) >= to ? to : `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-${String(s.getDate()).padStart(2, '0')}`)} ${f.getFullYear()}`;
}
const cuando = (s: string) => { const d = new Date(s); return isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }); };

export default function TeacherNomina({ userId }: { userId: number }) {
  const [data, setData] = useState<PayrollHistory | null | 'err'>(null);
  useEffect(() => { setData(null); getPayrollHistory(userId).then(setData).catch(() => setData('err')); }, [userId]);

  if (data === null) return <div className="mt-3 space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  if (data === 'err') return <div className="mt-3 rounded-2xl bg-surface p-4 text-sm text-content-3">No se pudo cargar la nómina.</div>;

  // Una fila por periodo (semana o mes): corrida + líneas manuales de ese mismo periodo.
  type Fila = { key: string; from: string; to: string; run: PayrollHistory['runs'][number] | null; extras: PayrollHistory['extras']; total: number };
  const filas = new Map<string, Fila>();
  const fila = (from: string, to: string) => { const k = `${from}_${to}`; if (!filas.has(k)) filas.set(k, { key: k, from, to, run: null, extras: [], total: 0 }); return filas.get(k)!; };
  for (const r of data.runs) { const f = fila(r.period_from, r.period_to); f.run = r; f.total += r.amount; }
  for (const e of data.extras) { const f = fila(e.period_from, e.period_to); f.extras.push(e); f.total += e.amount; }
  const lista = [...filas.values()].sort((a, b) => (a.from < b.from ? 1 : -1));
  const vacio = lista.length === 0 && data.payouts.length === 0;

  return (
    <div className="mt-3 rounded-2xl bg-surface p-4 shadow-sm" data-testid="profe-nomina">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-line pb-1.5">
        <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-content">
          <Wallet size={13} className="text-accent-ink" /> Nómina · últimos {data.months} meses
        </h3>
        <span className="text-[11px] text-content-3">Se registra desde <Link to="/finanzas" className="font-medium text-accent-ink hover:underline">Finanzas › Nómina</Link></span>
      </div>
      {!vacio && (
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div className="rounded-xl border border-line px-3 py-2"><div className="text-[11px] text-content-3">Sueldo y clases</div><div className="font-display text-lg font-bold text-content" data-testid="nomina-hist-runs">{money(data.totals.runs)}</div></div>
          <div className="rounded-xl border border-line px-3 py-2"><div className="text-[11px] text-content-3">Bonos / comisiones</div><div className="font-display text-lg font-bold text-content">{money(data.totals.extras)}</div></div>
          {data.totals.payouts > 0 && <div className="rounded-xl border border-line px-3 py-2"><div className="text-[11px] text-content-3">Comisión entregada</div><div className="font-display text-lg font-bold text-content">{money(data.totals.payouts)}</div></div>}
          <div className="rounded-xl border border-line bg-surface-2/60 px-3 py-2"><div className="text-[11px] text-content-3">Total</div><div className="font-display text-lg font-bold text-content" data-testid="nomina-hist-total">{money(data.totals.total)}</div></div>
        </div>
      )}
      {vacio ? (
        <EmptyState icon={Wallet} title="Sin nómina registrada" subtitle="Cuando se le registre un pago, bono o comisión en Finanzas › Nómina, aparece aquí." />
      ) : (
        <>
          {lista.length > 0 && (
            <div className="divide-y divide-line/60">
              {lista.map((f) => (
                <div key={f.key} className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 py-2.5 text-sm" data-testid="nomina-hist-fila">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium capitalize text-content">{semana(f.from, f.to)}</div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-content-3">
                      {f.run && f.run.salary != null && f.run.salary > 0 && <span>sueldo fijo {money(f.run.salary)}</span>}
                      {f.run && f.run.hours != null && f.run.hours > 0 && <span>{f.run.hours} h × {money(f.run.rate)} = {money(f.run.classes)}</span>}
                      {f.run && <span>· registrado {cuando(f.run.created_at)}{f.run.created_by_name ? ` por ${f.run.created_by_name}` : ''}</span>}
                      {!f.run && <span className="text-amber-600 dark:text-amber-400">sueldo / clases sin registrar</span>}
                    </div>
                    {f.extras.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {f.extras.map((x) => (
                          <span key={x.id} className="inline-flex items-center gap-1 rounded-full border border-line bg-surface-2/70 px-2 py-0.5 text-[11px] text-content-2" title={[x.note, x.created_by_name ? `registró ${x.created_by_name}` : ''].filter(Boolean).join(' · ')}>
                            <Gift size={10} className="text-content-3" /> {x.concept === 'otro' ? (x.label || 'Otro') : (x.label ? `${x.concept_label} · ${x.label}` : x.concept_label)} <b className="text-content">{money(x.amount)}</b>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="font-display text-base font-bold tabular-nums text-content">{money(f.total)}</span>
                </div>
              ))}
            </div>
          )}
          {data.payouts.length > 0 && (
            <div className="mt-4">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-content"><Percent size={12} className="text-accent-ink" /> Comisión entregada</div>
              <div className="divide-y divide-line/60">
                {data.payouts.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="min-w-0 flex-1 text-content-2">{dia(p.delivered_at, true)}{p.alumna ? ` · ${p.alumna}` : ''}{p.note ? <span className="text-content-3"> · {p.note}</span> : null}</span>
                    <span className="font-medium tabular-nums text-content">{money(p.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
